import { Fragment, useState, useMemo, useCallback, useId } from 'react';
import { Listbox, Transition } from '@headlessui/react';
import { Icon, Spinner } from '../';

import type { PresetSelectorProps, PresetOption } from './PresetSelector.types';
import { getFileTypeIcon } from '../../utils/file-formats';
import styles from './PresetSelector.module.css';

/**
 * Format preset dimensions for display
 */
function formatDimensions(dimensions?: { width: number; height: number }): string {
  if (!dimensions) return '';
  return `${dimensions.width} × ${dimensions.height}`;
}

/**
 * Format preset details for display
 */
function formatPresetDetails(preset: PresetOption): string {
  const parts: string[] = [];
  
  if (preset.fileType) {
    parts.push(preset.fileType.toUpperCase());
  }
  
  if (preset.dimensions) {
    parts.push(formatDimensions(preset.dimensions));
  }
  
  if (preset.fileSize) {
    parts.push(preset.fileSize);
  }
  
  return parts.join(', ');
}

/**
 * Get icon name for a preset based on its file type
 */
function getPresetIcon(preset: PresetOption): string {
  if (preset.isOriginal) {
    return 'insert_drive_file';
  }
  return getFileTypeIcon(`file.${preset.fileType || 'bin'}`);
}

/**
 * PresetSelector component for selecting download presets/formats
 *
 * @example
 * ```tsx
 * const presets = [
 *   { id: 'original', name: 'Original', fileType: 'jpg', isOriginal: true },
 *   { id: 'thumbnail', name: 'Thumbnail', fileType: 'jpg', dimensions: { width: 150, height: 150 } },
 *   { id: 'large', name: 'Large', fileType: 'png', dimensions: { width: 1920, height: 1080 } }
 * ];
 *
 * <PresetSelector
 *   presets={presets}
 *   value={selectedPreset}
 *   onChange={setSelectedPreset}
 *   showDetails
 *   label="Select format"
 * />
 * ```
 */
export function PresetSelector({
  presets,
  value,
  onChange,
  label,
  placeholder = 'Select a preset...',
  showDetails = false,
  size = 'md',
  disabled = false,
  error = false,
  errorMessage,
  loading = false,
  categories,
  clearable = true,
  searchable = false,
  fullWidth = false,
  className,
  'data-testid': testId,
  dataTestId,
  dataId,
}: PresetSelectorProps) {
  const id = useId();
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  // Find selected preset
  const selectedPreset = useMemo(
    () => presets.find((p) => p.id === value) || null,
    [presets, value]
  );

  // Filter presets based on search query
  const filteredPresets = useMemo(() => {
    if (!searchable || !query) {
      return presets;
    }
    const lowerQuery = query.toLowerCase();
    return presets.filter(
      (preset) =>
        preset.name.toLowerCase().includes(lowerQuery) ||
        (preset.fileType && preset.fileType.toLowerCase().includes(lowerQuery))
    );
  }, [presets, query, searchable]);

  // Filter categories based on search query
  const filteredCategories = useMemo(() => {
    if (!categories) return null;
    if (!searchable || !query) return categories;

    const lowerQuery = query.toLowerCase();
    return categories
      .map((category) => ({
        ...category,
        presets: category.presets.filter(
          (preset) =>
            preset.name.toLowerCase().includes(lowerQuery) ||
            (preset.fileType && preset.fileType.toLowerCase().includes(lowerQuery))
        ),
      }))
      .filter((category) => category.presets.length > 0);
  }, [categories, query, searchable]);

  // Handle selection change
  const handleChange = useCallback(
    (presetId: string) => {
      onChange(presetId);
      setQuery('');
      setIsOpen(false);
    },
    [onChange]
  );

  // Handle clear button click
  const handleClear = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      // When clearing, select the first original preset or just report empty
      const originalPreset = presets.find((p) => p.isOriginal);
      if (originalPreset) {
        onChange(originalPreset.id);
      }
    },
    [presets, onChange]
  );

  // Render a single option
  const renderOption = (preset: PresetOption, isSelected: boolean) => (
    <button
      type="button"
      key={preset.id}
      className={`${styles.option} ${isSelected ? styles.selected : ''}`}
      onClick={() => handleChange(preset.id)}
      data-testid={testId && `${testId}-option-${preset.id}`}
    >
      <span className={styles.optionContent}>
        {showDetails && (
          <span className={styles.optionIcon}>
            <Icon name={getPresetIcon(preset)} size="sm" />
          </span>
        )}
        <span className={styles.optionLabel}>
          <span className={styles.optionName}>{preset.name}</span>
          {showDetails && (
            <span className={styles.optionDetails}>
              {formatPresetDetails(preset)}
            </span>
          )}
        </span>
      </span>
      {preset.isOriginal && (
        <span className={styles.originalBadge}>Original</span>
      )}
      {isSelected && (
        <span className={styles.checkmark}>
          <Icon name="check" size="sm" />
        </span>
      )}
    </button>
  );

  // Build container class names
  const containerClasses = [
    styles.presetSelector,
    fullWidth && styles.fullWidth,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  // Determine what presets to render
  const hasCategories = filteredCategories && filteredCategories.length > 0;
  const hasPresets = filteredPresets.length > 0;
  const showNoResults =
    !loading && ((searchable && query && !hasPresets && !hasCategories) || (!hasPresets && !hasCategories));

  return (
    <div
      className={containerClasses}
      data-size={size}
      data-error={error}
      data-open={isOpen}
      data-testid={dataTestId || testId}
      data-id={dataId}
    >
      {label && (
        <label className={styles.label} id={`${id}-label`}>
          {label}
        </label>
      )}

      <Listbox
        value={value || ''}
        onChange={handleChange}
        disabled={disabled || loading}
      >
        {({ open }) => {
          // Sync open state for CSS
          if (open !== isOpen) {
            setIsOpen(open);
          }

          return (
            <>
              <Listbox.Button
                className={styles.trigger}
                aria-labelledby={label ? `${id}-label` : undefined}
                data-testid={testId && `${testId}-trigger`}
              >
                <span className={styles.triggerContent}>
                  {selectedPreset && showDetails && (
                    <span className={styles.triggerIcon}>
                      <Icon name={getPresetIcon(selectedPreset)} size="sm" />
                    </span>
                  )}
                  <span
                    className={`${styles.triggerText} ${!selectedPreset ? styles.placeholder : ''}`}
                  >
                    {selectedPreset ? selectedPreset.name : placeholder}
                  </span>
                </span>

                {clearable && selectedPreset && !selectedPreset.isOriginal && (
                  <button
                    type="button"
                    className={styles.clearButton}
                    onClick={handleClear}
                    aria-label="Clear selection"
                    data-testid={testId && `${testId}-clear`}
                  >
                    <Icon name="close" size="sm" />
                  </button>
                )}

                <span className={styles.chevron}>
                  <Icon name="keyboard_arrow_down" size="sm" />
                </span>
              </Listbox.Button>

              <Transition
                as={Fragment}
                leave={styles.transitionLeave}
                leaveFrom={styles.transitionLeaveActive}
                leaveTo={styles.transitionLeaveActive}
                enter={styles.transitionEnter}
                enterFrom={styles.transitionEnter}
                enterTo={styles.transitionEnterActive}
              >
                <Listbox.Options
                  className={styles.dropdown}
                  data-testid={testId && `${testId}-dropdown`}
                >
                  {searchable && (
                    <div className={styles.searchWrapper}>
                      <input
                        type="text"
                        className={styles.searchInput}
                        placeholder="Search presets..."
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        data-testid={testId && `${testId}-search`}
                      />
                    </div>
                  )}

                  {loading ? (
                    <div
                      className={styles.loadingWrapper}
                      data-testid={testId && `${testId}-loading`}
                    >
                      <Spinner size="sm" />
                      <span className={styles.loadingText}>Loading presets...</span>
                    </div>
                  ) : showNoResults ? (
                    <div
                      className={styles.noResults}
                      data-testid={testId && `${testId}-no-results`}
                    >
                      No presets found
                    </div>
                  ) : (
                    <div className={styles.optionsList}>
                      {hasCategories ? (
                        // Render categorized presets
                        filteredCategories!.map((category) => (
                          <Fragment key={category.label}>
                            <div className={styles.categoryLabel}>
                              {category.label}
                            </div>
                            {category.presets.map((preset) =>
                              renderOption(preset, preset.id === value)
                            )}
                          </Fragment>
                        ))
                      ) : (
                        // Render flat presets list
                        filteredPresets.map((preset) =>
                          renderOption(preset, preset.id === value)
                        )
                      )}
                    </div>
                  )}
                </Listbox.Options>
              </Transition>
            </>
          );
        }}
      </Listbox>

      {error && errorMessage && (
        <p
          className={styles.errorMessage}
          role="alert"
          data-testid={testId && `${testId}-error`}
        >
          {errorMessage}
        </p>
      )}
    </div>
  );
}

PresetSelector.displayName = 'PresetSelector';

export default PresetSelector;